import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FilterConfig, FilterChange, FilterOption } from '../filter-sidebar/filter-sidebar.component';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
	selector: 'app-filter-accordion',
	templateUrl: './filter-accordion.component.html',
	styleUrls: ['./filter-accordion.component.scss'],
	animations: [
		trigger('expandCollapse', [
			state('collapsed', style({
				height: '0',
				overflow: 'hidden',
				opacity: '0'
			})),
			state('expanded', style({
				height: '*',
				overflow: 'hidden',
				opacity: '1'
			})),
			transition('collapsed <=> expanded', [
				animate('200ms ease-in-out')
			])
		])
	]
})
export class FilterAccordionComponent implements OnInit, OnChanges {
	@Input() set config(value: FilterConfig) {
		this._config = value;
		this.initializeValues();
	}

	get config(): FilterConfig {
		return this._config;
	}

	private _config!: FilterConfig;
	@Output() filterChange = new EventEmitter<FilterChange>();
	@Output() expandToggled = new EventEmitter<void>();

	constructor(private router: Router, private route: ActivatedRoute) {}

	isExpanded: boolean = true;
	
	// Range input values
	minValue: number | null = null;
	maxValue: number | null = null;
	
	// Slider value
	sliderValue: number = 0;
	
	// View all/less functionality
	showAllOptions: boolean = false;
	readonly maxVisibleOptions: number = 5;

	// Search functionality
	searchQuery: string = '';
	filteredOptions: FilterOption[] = [];
	hasSearched: boolean = false;

	ngOnInit() {
		this.initializeValues();
		this.readViewAllStateFromUrl();
		this.initializeFilteredOptions();
		this.checkAndExpandAccordion();
	}

	private checkAndExpandAccordion() {
		// Check URL parameters for this filter
		const params = this.route.snapshot.queryParams;
		const hasUrlFilter = params[this.config.key];

		// Check if this filter has any selected options
		const hasSelectedOptions = this.config.options?.some(option => 
			option.selected && 
			option.value !== 'all' && 
			option.label.toLowerCase() !== 'all'
		);

		// Expand if either condition is true
		this.isExpanded = hasUrlFilter || hasSelectedOptions || false;
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['config'] && this.config) {
			this.initializeFilteredOptions();
			this.checkAndExpandAccordion();
		}
	}

	initializeValues() {
		if (this.config) {
			if (this.config.type === 'range') {
				this.sliderValue = this.config.initialValue !== undefined ? 
					this.config.initialValue : 
					(this.config.minValue !== undefined ? this.config.minValue : 0);
			}
		}
	}

	toggleExpand() {
		this.isExpanded = !this.isExpanded;
		this.expandToggled.emit();
	}

	toggleViewAll(event?: Event) {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		this.showAllOptions = !this.showAllOptions;
		// Temporarily disable URL update to prevent scrolling
		// this.updateUrlWithViewAllState();
	}

	private readViewAllStateFromUrl(): void {
		const params = this.route.snapshot.queryParams;
		const viewAllKey = `viewAll_${this.config.key}`;
		if (params[viewAllKey] === 'true') {
			this.showAllOptions = true;
		} else if (params[viewAllKey] === 'false') {
			this.showAllOptions = false;
		}
	}

	private updateUrlWithViewAllState(): void {
		const viewAllKey = `viewAll_${this.config.key}`;
		const queryParams: any = {};
		
		if (this.showAllOptions) {
			queryParams[viewAllKey] = 'true';
		} else {
			queryParams[viewAllKey] = 'false';
		}
		
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: queryParams,
			queryParamsHandling: 'merge',
			replaceUrl: true,
			skipLocationChange: false,
			state: { scrollPosition: window.scrollY }
		});
	}

	get visibleOptions() {
		if (!this.config.options) return [];
		
		// If search is active, return filtered options
		if (this.searchQuery.length && this.hasSearched) {
			return this.filteredOptions;
		}
		
		// Otherwise, return original options with view all/less logic
		if (this.showAllOptions || this.config.options.length <= this.maxVisibleOptions) {
			return this.config.options;
		}
		
		return this.config.options.slice(0, this.maxVisibleOptions);
	}

	get shouldShowViewAllButton() {
		// Don't show view all button when search is active or when filter is collapsed
		if ((this.searchQuery.length > 0 && this.hasSearched) || !this.isExpanded) {
			return false;
		}
		return this.config.options && this.config.options.length > this.maxVisibleOptions;
	}

	get selectedFiltersCount(): number {
		if (!this.config?.options) return 0;
		return this.config.options.filter(option => 
			option.selected && 
			option.value !== 'all' && 
			option.label.toLowerCase() !== 'all'
		).length;
	}

	// Search functionality methods
	initializeFilteredOptions(): void {
		if (this.config.options) {
			this.filteredOptions = [...this.config.options];
		}
	}

	onSearchChange(searchTerm: string): void {
		if (searchTerm.length > 0) {
			this.hasSearched = true;
		} else {
			this.hasSearched = false;
		}
		// This is called by the debounced search from common-search component
		this.searchQuery = searchTerm;
		this.filterOptions();
	}

	filterOptions(): void {
		if (!this.config.options) {
			this.filteredOptions = [];
			return;
		}

		// If search query is empty, show all options
		if (this.searchQuery.length === 0) {
			this.filteredOptions = [...this.config.options];
			return;
		}

		// Filter options based on search query
		this.filteredOptions = this.config.options.filter(option => {
			const searchLower = this.searchQuery.toLowerCase();
			const labelLower = option.label.toLowerCase();
			
			// Split search query into words
			const searchWords = searchLower.split(' ').filter(word => word.length > 0);
			
			// Check if all search words are present in the label
			return searchWords.every(word => labelLower.includes(word));
		});
	}

	clearSearch(): void {
		this.searchQuery = '';
		this.hasSearched = false;
		this.initializeFilteredOptions();
	}

	// Checkbox and radio handling
	onOptionChange(option: FilterOption) {
		if (this.config.type === 'radio') {
			// For radio buttons, deselect all other options
			if (this.config.options) {
				this.config.options.forEach((opt: FilterOption) => {
					opt.selected = opt === option;
				});
			}
		} else {
			// For checkboxes, handle "All" option specially
			if (option.value === 'all' || option.label.toLowerCase() === 'all') {
				this.handleAllOptionChange(option);
			} else {
				// Regular checkbox behavior
				option.selected = !option.selected;
				// Update "All" option state based on other selections
				this.updateAllOptionState();
			}
		}

		// Emit selected options
		this.emitCheckboxSelectionChange();
	}

	private handleAllOptionChange(allOption: FilterOption) {
		if (!this.config.options) return;

		// Toggle the "All" option
		allOption.selected = !allOption.selected;

		// If "All" is selected, select all other options (apply all filters)
		// If "All" is deselected, deselect all other options (show everything)
		this.config.options.forEach((opt: FilterOption) => {
			if (opt !== allOption && !opt.disabled) {
				opt.selected = allOption.selected ? true : false;
			}
		});
	}

	private updateAllOptionState() {
		if (!this.config.options) return;

		// Find the "All" option
		const allOption = this.config.options.find(opt => 
			opt.value === 'all' || opt.label.toLowerCase() === 'all'
		);

		if (!allOption) return;

		// Check if all other options are selected
		const otherOptions = this.config.options.filter(opt => 
			opt !== allOption && !opt.disabled
		);

		const allOtherSelected = otherOptions.every(opt => opt.selected);
		const anyOtherSelected = otherOptions.some(opt => opt.selected);

		// Update "All" option state
		if (allOtherSelected && otherOptions.length > 0) {
			allOption.selected = true; // "All" is selected when all others are selected
		} else {
			allOption.selected = false; // "All" is deselected when any other is not selected
		}
	}

	emitCheckboxSelectionChange() {
		if (!this.config.options) return;
		
		const selectedOptions = this.config.options.filter((opt) => opt.selected && !opt.disabled) || [];

		this.filterChange.emit({
			key: this.config.key,
			value: selectedOptions.length > 0 ? selectedOptions.map((opt) => opt.value) : null,
		});
	}

	// Range input handling
	onRangeInputChange() {
		const values: Record<string, number | null> = {};
		
		if (this.config.minKey) {
			values[this.config.minKey] = this.minValue;
		}
		
		if (this.config.maxKey) {
			values[this.config.maxKey] = this.maxValue;
		}
		
		this.filterChange.emit({
			key: this.config.key,
			value: values
		});
	}

	// Slider handling
	onSliderChange() {
		this.filterChange.emit({
			key: this.config.key,
			value: this.sliderValue
		});
	}

	resetFilter() {
		// Reset UI state based on filter type
		if (this.config.type === 'checkbox' || this.config.type === 'radio') {
			if (this.config.options) {
				this.config.options.forEach((opt: FilterOption) => {
					// Only reset enabled options
					if (!opt.disabled) {
						opt.selected = false;
					}
				});
			}

			this.filterChange.emit({
				key: this.config.key,
				value: null
			});
		} else if (this.config.type === 'input-range') {
			// Reset range input values
			this.minValue = null;
			this.maxValue = null;
			
			const resetPayload: Record<string, any> = {};

			if (this.config.minKey) {
				resetPayload[this.config.minKey] = null;
			}

			if (this.config.maxKey) {
				resetPayload[this.config.maxKey] = null;
			}

			this.filterChange.emit({
				key: this.config.key,
				value: resetPayload
			});
		} else if (this.config.type === 'range') {
			// Reset slider value
			this.sliderValue = this.config.initialValue !== undefined ?
				this.config.initialValue :
				(this.config.minValue !== undefined ? this.config.minValue : 0);

			this.filterChange.emit({
				key: this.config.key,
				value: this.sliderValue
			});
		}
	}
} 